import { TargetFieldsFragment_ } from 'models';
import { EsrsReportStandard } from '../../Report.types';
import { addEmptySpace, sanitizeText } from '../report-helper-functions';
import { adjustMDRReportData, ClimateTargetQuestionRefs } from '../../ReportUtils';
import { sortBy, uniqBy } from 'lodash';
import { tableBorders, TextStyle } from '../reportThemes';
import { Paragraph, Table, TableCell, TableRow, VerticalAlign } from 'docx';
import { MDRType } from 'containers/Esrs/pieces/DisclosureRequirements/MDR/MDRData';
import { mapUnitToCompanyCurrency, percentageToNumber } from 'containers/Esrs/utils';
import { createMDR, createTargetActionQuestion } from './mdr';

export const createTargets = (
  targets: TargetFieldsFragment_[] | undefined,
  targetQuestions: EsrsReportStandard['targetQuestions'],
  standardRef: string,
  companyCurrency: string
) => {
  if (!targets?.length) return [];

  const displayedTargets = targets.flatMap((target) => {
    const targetSection = createTarget(target, targetQuestions, standardRef, companyCurrency);
    return [targetSection, addEmptySpace(200)];
  });

  const summaryTable = createTargetsSummaryTable(targets, companyCurrency);

  return [...summaryTable, ...displayedTargets.flat()];
};

export const createTarget = (
  target: TargetFieldsFragment_,
  targetQuestions: EsrsReportStandard['targetQuestions'],
  standardRef: string,
  companyCurrency: string
) => {
  const isClimateChange = standardRef === 'ESRS-E1';
  const reportTargetsMDR = adjustMDRReportData({
    target,
    isDocx: true,
    isClimateChange,
    flatTargetQuestions: targetQuestions,
    companyCurrency,
  });

  const filteredTargetQuestions = isClimateChange
    ? targetQuestions.filter((question) => !ClimateTargetQuestionRefs.includes(question.reference))
    : targetQuestions;

  const groupTQ = uniqBy(
    filteredTargetQuestions.map((tq) => tq.group)?.filter((tq) => !!tq) ?? [],
    'reference'
  );
  const groupedTargetQuestions = groupTQ.map((group) => ({
    ...group,
    targetQuestions: filteredTargetQuestions.filter((q) => q.groupRef === group?.reference) ?? [],
  }));

  const singleTargetQuestions = filteredTargetQuestions.filter((tq) => tq.groupRef === null);

  const title = sanitizeText(target.name);

  const titleParagraph = new Paragraph({
    text: title,
    style: TextStyle.h3,
    spacing: {
      after: 300,
    },
  });

  const titleMDRT = new Paragraph({
    text: 'MDR-T',
    style: TextStyle.h4,
    spacing: {
      after: 200,
    },
  });

  const titleOtherDisclosures = new Paragraph({
    text: 'Other Disclosures',
    style: TextStyle.h4,
    spacing: {
      after: 200,
    },
  });

  const displayedMDRT = reportTargetsMDR.flatMap((mdr) => {
    const mdrtSection = createMDR({ mdr: mdr as MDRType, target });
    return [...mdrtSection, addEmptySpace(200)];
  });

  const displayedTargetQuestionGroup = groupedTargetQuestions.flatMap((group) => {
    const groupQuestionTitle = new Paragraph({
      text: group.title,
      style: TextStyle.h4,
      spacing: {
        after: 150,
      },
    });
    const groupTargetQuestionsSection = group.targetQuestions?.flatMap((question) => {
      const targetQuestionSection = createTargetActionQuestion(
        question,
        target.answers,
        companyCurrency
      );
      return [...targetQuestionSection, addEmptySpace(100)];
    });
    return [groupQuestionTitle, ...groupTargetQuestionsSection, addEmptySpace(100)];
  });

  const displayedSingleTargetQuestion = singleTargetQuestions.flatMap((question) => {
    const targetQuestionSection = createTargetActionQuestion(
      question,
      target.answers,
      companyCurrency
    );
    return [...targetQuestionSection, addEmptySpace(100)];
  });

  return [
    titleParagraph,
    titleMDRT,
    ...displayedMDRT,
    titleOtherDisclosures,
    ...displayedTargetQuestionGroup,
    ...displayedSingleTargetQuestion,
  ];
};

export const createTargetsSummaryTable = (
  targets: TargetFieldsFragment_[],
  companyCurrency: string
) => {
  if (!targets?.length) return [];

  const nbMilestoneYears = Math.max(...targets.map((t) => t.milestones?.length ?? 0));
  const milestoneMap = Array.from({ length: nbMilestoneYears }, (_, i) => i);

  const titleParagraph = new Paragraph({
    text: 'Summary',
    style: TextStyle.h4,
    spacing: {
      after: 100,
    },
  });

  const columnsNb = 2 + nbMilestoneYears;
  const firstColumnWidth = 10000 - columnsNb * 1000;

  const targetsTable = new Table({
    columnWidths: [firstColumnWidth, 1000, 1000, ...milestoneMap.map(() => 1000)],
    margins: {
      top: 60,
      bottom: 60,
      right: 60,
      left: 60,
    },
    borders: tableBorders,
    rows: [
      new TableRow({
        tableHeader: true,
        children: [
          new TableCell({
            verticalAlign: VerticalAlign.CENTER,
            children: [
              new Paragraph({
                text: 'Target name',
                style: TextStyle.tableTitle,
              }),
            ],
          }),
          new TableCell({
            verticalAlign: VerticalAlign.CENTER,
            children: [
              new Paragraph({
                text: 'Related metric',
                style: TextStyle.tableTitle,
              }),
            ],
          }),
          new TableCell({
            verticalAlign: VerticalAlign.CENTER,
            children: [
              new Paragraph({
                text: 'Baseline value',
                style: TextStyle.tableTitle,
              }),
            ],
          }),
          new TableCell({
            verticalAlign: VerticalAlign.CENTER,
            children: [
              new Paragraph({
                text: 'Baseline year',
                style: TextStyle.tableTitle,
              }),
            ],
          }),
          ...milestoneMap.map(
            (index) =>
              new TableCell({
                verticalAlign: VerticalAlign.CENTER,
                children: [
                  new Paragraph({
                    text: index === nbMilestoneYears - 1 ? 'End target' : `Milestone ${index + 1}`,
                    style: TextStyle.tableTitle,
                  }),
                ],
              })
          ),
        ],
      }),
      ...targets.map((target) => createTargetRows(target, milestoneMap, companyCurrency)),
    ].flat(),
  });

  return [titleParagraph, targetsTable];
};

export const createTargetRows = (
  target: TargetFieldsFragment_,
  milestoneMap: number[],
  companyCurrency: string
) => {
  const baseline = target.keyResults.find((kr) => kr.reportingUnitId === null);
  const unit = mapUnitToCompanyCurrency(target.metric?.unitOfMeasurement ?? 't', companyCurrency);

  const displayedRows = [
    new TableRow({
      children: [
        new TableCell({
          verticalAlign: VerticalAlign.TOP,
          children: [
            new Paragraph({
              text: sanitizeText(target.name),
              style: TextStyle.body,
            }),
          ],
        }),
        new TableCell({
          verticalAlign: VerticalAlign.TOP,
          children: [
            new Paragraph({
              text: `${target.metric?.title}`,
              style: TextStyle.body,
            }),
            new Paragraph({
              text: `(${unit === 't' ? 'tonnes' : unit})`,
              style: TextStyle.body,
            }),
          ],
        }),
        new TableCell({
          verticalAlign: VerticalAlign.TOP,
          children: [
            new Paragraph({
              text: baseline?.baseline ?? 'NA',
              style: TextStyle.body,
            }),
          ],
        }),
        new TableCell({
          verticalAlign: VerticalAlign.TOP,
          children: [
            new Paragraph({
              text: baseline?.baseYear ? `${new Date(baseline?.baseYear).getUTCFullYear()}` : 'NA',
              style: TextStyle.body,
            }),
          ],
        }),
        ...milestoneMap.map((index) => {
          const milestones = sortBy(target.milestones, 'year');
          const milestone =
            milestones.length === 1 && index === milestoneMap.length - 1
              ? milestones[0]
              : milestones[index];
          const milestoneResult = milestone?.milestoneResults.find(
            (mr) => mr.reportingUnitId === null
          )?.value;
          const targetValue =
            Number(baseline?.baseline) !== 0
              ? percentageToNumber(milestoneResult, Number(baseline?.baseline))
              : milestoneResult;
          return new TableCell({
            verticalAlign: VerticalAlign.TOP,
            children: [
              new Paragraph({
                text: !milestone
                  ? ''
                  : milestones.length === 1 && index != milestoneMap.length - 1
                    ? ''
                    : isNaN(milestoneResult) || isNaN(targetValue)
                      ? 'NA'
                      : `${targetValue} (${milestone.year})`,
                style: TextStyle.body,
              }),
            ],
          });
        }),
      ],
    }),
  ];
  return displayedRows.flat();
};
